/**
 * Admin analytics service — fetches aggregated stats via Supabase RPC functions.
 * All RPCs use SECURITY DEFINER and verify the caller is carsten@propermiles.com.
 */
import { supabase } from '../config/supabase';

/* ───────────────── Time Range Helpers ───────────────── */

const RANGES = {
  today: () => new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
  '7d': () => new Date(Date.now() - 7 * 86400000).toISOString(),
  '30d': () => new Date(Date.now() - 30 * 86400000).toISOString(),
  '90d': () => new Date(Date.now() - 90 * 86400000).toISOString(),
  all: () => new Date('2020-01-01').toISOString(),
};

export function getSinceDate(rangeKey) {
  return (RANGES[rangeKey] || RANGES['30d'])();
}

/* ───────────────── MRR Calculation ───────────────── */

const TIER_PRICES = {
  free: 0,
  enthusiast: 9,
  collector: 19,
  showroom: 39,
  team: 99,
};

export function calculateMRR(tierCounts) {
  if (!tierCounts) return 0;
  return Object.entries(tierCounts).reduce((sum, [tier, count]) => {
    return sum + (TIER_PRICES[tier] || 0) * count;
  }, 0);
}

/* ───────────────── RPC Wrappers ───────────────── */

export async function fetchGrowthStats(since) {
  const { data, error } = await supabase.rpc('admin_growth_stats', { since });
  if (error) throw error;
  return data;
}

export async function fetchSignupsOverTime(since) {
  const { data, error } = await supabase.rpc('admin_signups_over_time', { since });
  if (error) throw error;
  return data || [];
}

export async function fetchFeatureUsage(since) {
  const { data, error } = await supabase.rpc('admin_feature_usage', { since });
  if (error) throw error;
  return data;
}

export async function fetchUserDemographics() {
  const { data, error } = await supabase.rpc('admin_user_demographics');
  if (error) throw error;
  return data;
}

export async function fetchVehicleAnalytics() {
  const { data, error } = await supabase.rpc('admin_vehicle_analytics');
  if (error) throw error;
  return data;
}

export async function fetchEngagementStats() {
  const { data, error } = await supabase.rpc('admin_engagement_stats');
  if (error) throw error;
  return data;
}

export async function fetchRecentSignups() {
  const { data, error } = await supabase.rpc('admin_recent_signups');
  if (error) throw error;
  return data || [];
}

export async function fetchAllUsers() {
  const { data, error } = await supabase.rpc('admin_all_users');
  if (error) throw error;
  return data || [];
}

export async function fetchAiUsage(since) {
  const { data, error } = await supabase.rpc('admin_ai_usage', { since });
  if (error) throw error;
  return data || [];
}

/* ───────────────── Super Admin CRM ───────────────── */

export async function fetchSuperAdminUsers() {
  const { data, error } = await supabase.rpc('admin_super_admin_users');
  if (error) throw error;
  return data || [];
}

export async function fetchSuperAdminVehicles() {
  const { data, error } = await supabase.rpc('admin_super_admin_vehicles');
  if (error) throw error;
  return data || [];
}

/* ───────────────── Announcements ───────────────── */

export async function createAnnouncement({ title, body, recipientUserIds }) {
  // 1. Insert announcement
  const { data: ann, error: annErr } = await supabase
    .from('announcements')
    .insert({ title, body, sent_by: (await supabase.auth.getUser()).data.user?.id, recipient_count: recipientUserIds.length })
    .select('id')
    .single();
  if (annErr) throw annErr;

  // 2. Insert recipients in batches of 500
  const announcementId = ann.id;
  const rows = recipientUserIds.map(uid => ({ announcement_id: announcementId, user_id: uid }));
  for (let i = 0; i < rows.length; i += 500) {
    const batch = rows.slice(i, i + 500);
    const { error: recErr } = await supabase.from('announcement_recipients').insert(batch);
    if (recErr) throw recErr;
  }

  // 3. Invoke edge function to send push notifications
  try {
    await supabase.functions.invoke('send-announcement-push', {
      body: { announcement_id: announcementId },
    });
  } catch (pushErr) {
    console.error('Push send failed (announcement still created):', pushErr);
  }

  return announcementId;
}

export async function fetchAnnouncement(announcementId) {
  const { data, error } = await supabase.rpc('get_announcement', { p_announcement_id: announcementId });
  if (error) throw error;
  return data?.[0] || null;
}

export async function fetchUnreadAnnouncement() {
  const userId = (await supabase.auth.getUser()).data.user?.id;
  if (!userId) return null;
  const { data, error } = await supabase
    .from('announcement_recipients')
    .select('announcement_id')
    .eq('user_id', userId)
    .is('read_at', null)
    .order('created_at', { ascending: false })
    .limit(1);
  if (error || !data || data.length === 0) return null;
  return data[0].announcement_id;
}

export async function markAnnouncementRead(announcementId) {
  const userId = (await supabase.auth.getUser()).data.user?.id;
  if (!userId) return;
  await supabase
    .from('announcement_recipients')
    .update({ read_at: new Date().toISOString() })
    .eq('announcement_id', announcementId)
    .eq('user_id', userId)
    .is('read_at', null);
}

/* ───────────────── AI Cost Calculation ───────────────── */

const MODEL_PRICING = {
  'gpt-4o': { input: 2.50 / 1e6, output: 10.00 / 1e6 },
  'gpt-4o-mini': { input: 0.15 / 1e6, output: 0.60 / 1e6 },
};

export function calculateAiCost(usageRows) {
  if (!usageRows || usageRows.length === 0) return { totalCost: 0, totalCalls: 0, byModel: {} };
  let totalCost = 0;
  let totalCalls = 0;
  const byModel = {};
  for (const row of usageRows) {
    const pricing = MODEL_PRICING[row.model] || MODEL_PRICING['gpt-4o-mini'];
    const inputCost = Number(row.total_prompt_tokens) * pricing.input;
    const outputCost = Number(row.total_completion_tokens) * pricing.output;
    const modelCost = inputCost + outputCost;
    totalCost += modelCost;
    totalCalls += Number(row.total_calls);
    byModel[row.model] = modelCost;
  }
  return { totalCost, totalCalls, byModel };
}

/* ───────────────── Parallel Fetch ───────────────── */

export async function fetchAllAdminStats(rangeKey) {
  const since = getSinceDate(rangeKey);

  const [growth, signups, features, demographics, vehicles, engagement, recentSignups, aiUsage] =
    await Promise.allSettled([
      fetchGrowthStats(since),
      fetchSignupsOverTime(since),
      fetchFeatureUsage(since),
      fetchUserDemographics(),
      fetchVehicleAnalytics(),
      fetchEngagementStats(),
      fetchRecentSignups(),
      fetchAiUsage(since),
    ]);

  return {
    growth: growth.status === 'fulfilled' ? growth.value : null,
    signups: signups.status === 'fulfilled' ? signups.value : null,
    features: features.status === 'fulfilled' ? features.value : null,
    demographics: demographics.status === 'fulfilled' ? demographics.value : null,
    vehicles: vehicles.status === 'fulfilled' ? vehicles.value : null,
    engagement: engagement.status === 'fulfilled' ? engagement.value : null,
    recentSignups: recentSignups.status === 'fulfilled' ? recentSignups.value : null,
    aiUsage: aiUsage.status === 'fulfilled' ? aiUsage.value : null,
    errors: [growth, signups, features, demographics, vehicles, engagement, recentSignups, aiUsage]
      .filter((r) => r.status === 'rejected')
      .map((r) => r.reason?.message || 'Unknown error'),
  };
}
